import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Calculator, TrendingUp, AlertCircle, CheckCircle2, Info, Loader2, Settings, Plus, Pencil, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { AmountInput } from '../components/ui/amount-input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default nisab source for a new year (stored as entered)
const DEFAULT_SOURCE = "O'zbekiston Musulmonlar Idorasi";

function Zakat() {
  const { t } = useTranslation('zakat');
  const MONTHS_SHORT = t('common:monthsShort', { returnObjects: true });
  const { user: currentUser } = useAuth();
  const isEmployee = currentUser?.role === 'employee';
  const isAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState('UZS');

  // Custom calculator
  const [customAmount, setCustomAmount] = useState('');
  const [customCurrency, setCustomCurrency] = useState('UZS');
  const [customResult, setCustomResult] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);

  // Admin Nisab management
  const [nisabList, setNisabList] = useState([]);
  const [nisabLoading, setNisabLoading] = useState(false);
  const [editingNisab, setEditingNisab] = useState(null); // year being edited
  const [nisabForm, setNisabForm] = useState({ year: '', nisab_uzs: '', nisab_usd: '', gold_gram_uzs: '', source: '' });
  const [savingNisab, setSavingNisab] = useState(false);
  const [showAddNisab, setShowAddNisab] = useState(false);

  const fmtUzs = (v) => `${Math.round(v).toLocaleString('uz-UZ')} UZS`;
  const fmtUsd = (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtAmount = (uzs, usd) => displayCurrency === 'UZS' ? fmtUzs(uzs) : fmtUsd(usd);

  const fetchZakat = useCallback(async () => {
    if (!isEmployee) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/zakat/calculate?year=${selectedYear}`, { withCredentials: true });
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setResult(null);
      } else {
        toast.error(err.response?.data?.detail || t('common:status.error'));
      }
    } finally {
      setLoading(false);
    }
  }, [selectedYear, isEmployee, t]);

  const fetchNisabList = useCallback(async () => {
    if (!isAdmin) return;
    setNisabLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/zakat/nisab`, { withCredentials: true });
      setNisabList(Array.isArray(res.data) ? res.data : []);
    } catch { /* silent */ }
    finally { setNisabLoading(false); }
  }, [isAdmin]);

  useEffect(() => {
    fetchNisabList();
  }, [fetchNisabList]);

  useEffect(() => {
    fetchZakat();
  }, [fetchZakat]);

  const handleToggleZakatPaid = async () => {
    const newPaid = !result?.is_paid;
    setResult(prev => ({ ...prev, is_paid: newPaid }));
    try {
      await axios.post(`${API_URL}/api/zakat/mark`, { year: selectedYear, is_paid: newPaid }, { withCredentials: true });
    } catch {
      setResult(prev => ({ ...prev, is_paid: !newPaid }));
      toast.error(t('toast.markFailed'));
    }
  };

  const handleCustomCalculate = async () => {
    const amt = parseFloat(customAmount);
    if (!amt || amt <= 0) {
      toast.error(t('calculator.amountRequired'));
      return;
    }
    setCustomLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/zakat/calculate-custom?amount=${amt}&currency=${customCurrency}&year=${selectedYear}`,
        { withCredentials: true }
      );
      setCustomResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    } finally {
      setCustomLoading(false);
    }
  };

  const startEditNisab = (row) => {
    setEditingNisab(row.year);
    setNisabForm({
      year: String(row.year),
      nisab_uzs: String(row.nisab_uzs),
      nisab_usd: String(row.nisab_usd || ''),
      gold_gram_uzs: String(row.gold_gram_uzs || ''),
      source: row.source || '',
    });
  };

  const cancelEditNisab = () => {
    setEditingNisab(null);
    setShowAddNisab(false);
    setNisabForm({ year: '', nisab_uzs: '', nisab_usd: '', gold_gram_uzs: '', source: '' });
  };

  const handleSaveNisab = async () => {
    if (!nisabForm.year || !nisabForm.nisab_uzs) {
      toast.error(t('nisab.required'));
      return;
    }
    setSavingNisab(true);
    try {
      await axios.post(`${API_URL}/api/zakat/nisab`, {
        year: parseInt(nisabForm.year),
        nisab_uzs: parseFloat(nisabForm.nisab_uzs),
        nisab_usd: nisabForm.nisab_usd ? parseFloat(nisabForm.nisab_usd) : null,
        gold_gram_uzs: nisabForm.gold_gram_uzs ? parseFloat(nisabForm.gold_gram_uzs) : null,
        source: nisabForm.source || null,
      }, { withCredentials: true });
      toast.success(t('nisab.saved', { year: nisabForm.year }));
      cancelEditNisab();
      fetchNisabList();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    } finally {
      setSavingNisab(false);
    }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2023; y--) years.push(y);

  const chartData = result?.monthly?.map((m, i) => ({
    name: MONTHS_SHORT[i],
    earnings: displayCurrency === 'UZS' ? Math.round(m.earnings_uzs) : m.earnings_usd,
  })) || [];

  const nisabPerMonth = result ? (displayCurrency === 'UZS' ? Math.round(result.nisab_uzs / 12) : +(result.nisab_usd / 12).toFixed(2)) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Calculator className="h-7 w-7 text-emerald-600" />
            {t('title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="UZS">UZS</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={String(y)}>{t('yearLabel', { year: y })}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Info banner */}
      <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-emerald-800 dark:text-emerald-300">
              <p className="font-medium mb-1">{t('about.title')}</p>
              <p>{t('about.text')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee earnings-based calculation */}
      {isEmployee && (
        <>
          {loading ? (
            <Card>
              <CardContent className="py-12 flex justify-center items-center gap-2 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" /> {t('calculating')}
              </CardContent>
            </Card>
          ) : result ? (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Annual earnings */}
                <Card>
                  <CardContent className="pt-5">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('summary.annualEarnings')}</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                      {fmtAmount(result.annual_earnings_uzs, result.annual_earnings_usd)}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">{t('yearLabel', { year: selectedYear })}</div>
                  </CardContent>
                </Card>

                {/* Nisab threshold */}
                <Card>
                  <CardContent className="pt-5">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('summary.nisabThreshold')}</div>
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {fmtAmount(result.nisab_uzs, result.nisab_usd)}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {result.nisab_gold_gram_uzs ? `85g × ${fmtUzs(result.nisab_gold_gram_uzs)}/g` : t('summary.goldGrams')}
                    </div>
                  </CardContent>
                </Card>

                {/* Status */}
                <Card>
                  <CardContent className="pt-5">
                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('common:fields.status')}</div>
                    {result.exceeds_nisab ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                        <span className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{t('nisabReached')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-6 w-6 text-slate-400" />
                        <span className="text-lg font-semibold text-slate-500">{t('nisabNotReached')}</span>
                      </div>
                    )}
                    <div className="text-xs text-slate-400 mt-1">
                      {result.exceeds_nisab
                        ? t('summary.obligatory')
                        : t('summary.shortfall', { amount: fmtAmount(result.nisab_uzs - result.annual_earnings_uzs, result.nisab_usd - result.annual_earnings_usd) })}
                    </div>
                  </CardContent>
                </Card>

                {/* Zakat amount */}
                <Card className={result.exceeds_nisab ? 'ring-2 ring-emerald-500 dark:ring-emerald-400' : ''}>
                  <CardContent className="pt-5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('summary.zakatAmount')}</div>
                        <div className={`text-2xl font-bold ${result.exceeds_nisab ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                          {result.exceeds_nisab ? fmtAmount(result.zakat_uzs, result.zakat_usd) : '—'}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {result.nisab_source || t('defaultSource')}
                        </div>
                      </div>
                      {result.exceeds_nisab && (
                        <button
                          onClick={handleToggleZakatPaid}
                          title={result.is_paid ? t('paid.marked') : t('paid.mark')}
                          className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-md border-2 flex items-center justify-center transition-colors ${
                            result.is_paid
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'border-slate-300 dark:border-slate-600 text-transparent hover:border-emerald-400'
                          }`}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {result.exceeds_nisab && (
                      <div className={`mt-2 text-xs font-medium ${result.is_paid ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                        {result.is_paid ? `✓ ${t('paid.paid', { year: selectedYear })}` : t('paid.unpaid', { year: selectedYear })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Monthly chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="h-5 w-5" />
                    {t('chart.title', { year: selectedYear })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => displayCurrency === 'UZS' ? `${(v / 1000000).toFixed(0)}M` : `$${v}`} />
                        <Tooltip
                          formatter={(v) => displayCurrency === 'UZS' ? fmtUzs(v) : fmtUsd(v)}
                          labelFormatter={(l) => `${l} ${selectedYear}`}
                        />
                        {nisabPerMonth > 0 && (
                          <ReferenceLine y={nisabPerMonth} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: t('chart.nisabLine'), position: 'right', fontSize: 11, fill: '#f59e0b' }} />
                        )}
                        <Bar dataKey="earnings" radius={[4, 4, 0, 0]} name={t('chart.earnings')}>
                          {chartData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.earnings > 0 ? '#10b981' : '#e2e8f0'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Monthly breakdown table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{t('breakdown.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-700">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('common:fields.month')}</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('breakdown.earnings')}</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('breakdown.cumulative')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let cumulative = 0;
                          return result.monthly.map((m, i) => {
                            cumulative += displayCurrency === 'UZS' ? m.earnings_uzs : m.earnings_usd;
                            return (
                              <tr key={i} className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                <td className="px-4 py-2 font-medium text-slate-700 dark:text-slate-300">{MONTHS_SHORT[i]}</td>
                                <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">
                                  {fmtAmount(m.earnings_uzs, m.earnings_usd)}
                                </td>
                                <td className="px-4 py-2 text-right font-medium text-slate-900 dark:text-slate-100">
                                  {displayCurrency === 'UZS' ? fmtUzs(cumulative) : fmtUsd(cumulative)}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot className="bg-emerald-50 dark:bg-emerald-900/20 font-semibold">
                        <tr>
                          <td className="px-4 py-3 text-emerald-700 dark:text-emerald-400">{t('common:fields.total')}</td>
                          <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-400">
                            {fmtAmount(result.annual_earnings_uzs, result.annual_earnings_usd)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-400">
                            {result.exceeds_nisab
                              ? t('breakdown.zakat', { amount: fmtAmount(result.zakat_uzs, result.zakat_usd) })
                              : t('nisabNotReached')}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400">{t('noData.title', { year: selectedYear })}</p>
                <p className="text-sm text-slate-400 mt-1">{t('noData.hint')}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Custom calculator — available to all */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Calculator className="h-5 w-5" />
            {t('calculator.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Phone: amount + currency on one row, the button full width below; wider: one row */}
          <div className="flex flex-wrap items-end gap-3 sm:flex-nowrap sm:gap-4">
            <div className="flex-1 min-w-0 space-y-1.5">
              <Label htmlFor="custom-amount">{t('calculator.amount')}</Label>
              <AmountInput
                id="custom-amount"
                placeholder={t('calculator.amountRequired')}
                value={customAmount}
                onChange={(val) => setCustomAmount(val)}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomCalculate()}
              />
            </div>
            <div className="w-[100px] shrink-0 space-y-1.5">
              <Label>{t('common:fields.currency')}</Label>
              <Select value={customCurrency} onValueChange={setCustomCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UZS">UZS</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCustomCalculate} disabled={customLoading} className="w-full sm:w-auto">
              {customLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Calculator className="h-4 w-4 mr-1.5" />}
              {t('calculator.calculate')}
            </Button>
          </div>

          {customResult && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <div className="text-sm text-slate-500 mb-1">{t('calculator.enteredAmount')}</div>
                <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  {fmtAmount(customResult.amount_uzs, customResult.amount_usd)}
                </div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <div className="text-sm text-slate-500 mb-1">{t('calculator.nisabYear', { year: selectedYear })}</div>
                <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
                  {fmtAmount(customResult.nisab_uzs, customResult.nisab_usd)}
                </div>
                <div className="text-xs mt-1">
                  {customResult.exceeds_nisab
                    ? <span className="text-emerald-500">✓ {t('nisabReached')}</span>
                    : <span className="text-slate-400">{t('nisabNotReached')}</span>}
                </div>
              </div>
              <div className={`rounded-lg p-4 ${customResult.exceeds_nisab ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-slate-50 dark:bg-slate-800'}`}>
                <div className="text-sm text-slate-500 mb-1">{t('calculator.zakat')}</div>
                <div className={`text-lg font-bold ${customResult.exceeds_nisab ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`}>
                  {customResult.exceeds_nisab ? fmtAmount(customResult.zakat_uzs, customResult.zakat_usd) : '—'}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin Nisab Management */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Settings className="h-5 w-5" />
                {t('nisab.title')}
              </CardTitle>
              {!showAddNisab && (
                <Button size="sm" variant="outline" onClick={() => { setShowAddNisab(true); setEditingNisab(null); setNisabForm({ year: String(new Date().getFullYear() + 1), nisab_uzs: '', nisab_usd: '', gold_gram_uzs: '', source: DEFAULT_SOURCE }); }}>
                  <Plus className="h-4 w-4 mr-1" /> {t('nisab.addYear')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Add new row form */}
            {showAddNisab && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-3">{t('nisab.addTitle')}</div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">{t('common:fields.year')}</Label>
                    <Input type="number" value={nisabForm.year} onChange={(e) => setNisabForm(f => ({ ...f, year: e.target.value }))} placeholder="2027" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('nisab.uzs')}</Label>
                    <AmountInput value={nisabForm.nisab_uzs} onChange={(val) => setNisabForm(f => ({ ...f, nisab_uzs: val }))} placeholder="121000000" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('nisab.usd')}</Label>
                    <AmountInput value={nisabForm.nisab_usd} onChange={(val) => setNisabForm(f => ({ ...f, nisab_usd: val }))} placeholder="9950" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('nisab.gold')}</Label>
                    <AmountInput value={nisabForm.gold_gram_uzs} onChange={(val) => setNisabForm(f => ({ ...f, gold_gram_uzs: val }))} placeholder="1423529" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('nisab.source')}</Label>
                    <Input value={nisabForm.source} onChange={(e) => setNisabForm(f => ({ ...f, source: e.target.value }))} placeholder={t('nisab.source')} />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={handleSaveNisab} disabled={savingNisab}>
                    {savingNisab ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    {t('common:actions.save')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEditNisab}>
                    <X className="h-4 w-4 mr-1" /> {t('common:actions.cancel')}
                  </Button>
                </div>
              </div>
            )}

            {/* Nisab table */}
            {nisabLoading ? (
              <div className="py-8 flex justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-700">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('common:fields.year')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('nisab.uzs')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('nisab.usd')}</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('nisab.gold')}</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('nisab.source')}</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-600 dark:text-slate-400">{t('nisab.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nisabList.map((row) => (
                      <tr key={row.year} className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                        {editingNisab === row.year ? (
                          <>
                            <td className="px-4 py-2 font-medium">{row.year}</td>
                            <td className="px-4 py-2"><AmountInput className="h-8 text-right" value={nisabForm.nisab_uzs} onChange={(val) => setNisabForm(f => ({ ...f, nisab_uzs: val }))} /></td>
                            <td className="px-4 py-2"><AmountInput className="h-8 text-right" value={nisabForm.nisab_usd} onChange={(val) => setNisabForm(f => ({ ...f, nisab_usd: val }))} /></td>
                            <td className="px-4 py-2"><AmountInput className="h-8 text-right" value={nisabForm.gold_gram_uzs} onChange={(val) => setNisabForm(f => ({ ...f, gold_gram_uzs: val }))} /></td>
                            <td className="px-4 py-2"><Input className="h-8" value={nisabForm.source} onChange={(e) => setNisabForm(f => ({ ...f, source: e.target.value }))} /></td>
                            <td className="px-4 py-2 text-center">
                              <div className="flex justify-center gap-1">
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleSaveNisab} disabled={savingNisab}>
                                  {savingNisab ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5 text-emerald-600" />}
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={cancelEditNisab}>
                                  <X className="h-3.5 w-3.5 text-slate-400" />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2 font-medium text-slate-700 dark:text-slate-300">{row.year}</td>
                            <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{fmtUzs(row.nisab_uzs)}</td>
                            <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{row.nisab_usd ? fmtUsd(row.nisab_usd) : '—'}</td>
                            <td className="px-4 py-2 text-right text-slate-600 dark:text-slate-400">{row.gold_gram_uzs ? fmtUzs(row.gold_gram_uzs) : '—'}</td>
                            <td className="px-4 py-2 text-slate-500 dark:text-slate-400 text-xs">{row.source || '—'}</td>
                            <td className="px-4 py-2 text-center">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEditNisab(row)}>
                                <Pencil className="h-3.5 w-3.5 text-slate-500" />
                              </Button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                    {nisabList.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{t('nisab.empty')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default Zakat;
